(function () {
  'use strict';

  var CONFIG_ID = 'aa-exact-media-reviews-config';
  var ROOT_CLASS = 'aa-exact-media-reviews';
  var CACHE_TTL = 60 * 60 * 1000;
  var MAX_VISIBLE_REVIEWS = 8;
  var FIND_TIMEOUT = 10000;
  var REQUEST_TIMEOUT = 4000;
  var started = false;

  function track(eventName, detail) {
    var payload = Object.assign({ event: eventName }, detail || {});
    window.dispatchEvent(new CustomEvent(eventName, { detail: payload }));
    if (Array.isArray(window.dataLayer)) window.dataLayer.push(payload);
  }

  function eventDetail(config, extra) {
    return Object.assign({
      product_id: String(config.productId),
      product_handle: String(config.productHandle || ''),
      locale: String(config.locale || 'en'),
      device_class: window.matchMedia('(max-width: 749px)').matches ? 'mobile' : 'desktop',
      source_mode: 'loox_aggregated'
    }, extra || {});
  }

  function trackError(config, code) {
    track('aa_exact_media_reviews_error', eventDetail(config, { error_code: code }));
  }

  function decodeText(value) {
    var textarea = document.createElement('textarea');
    textarea.innerHTML = String(value || '');
    return textarea.value;
  }

  function parseConfig() {
    var node = document.getElementById(CONFIG_ID);
    if (!node) return null;
    try {
      var config = JSON.parse(node.textContent);
      if (config.copy) {
        Object.keys(config.copy).forEach(function (key) {
          if (typeof config.copy[key] === 'string') config.copy[key] = decodeText(config.copy[key]);
        });
      }
      return config.productId && config.publicStoreId && config.copy ? config : null;
    } catch (error) {
      return null;
    }
  }

  function findAggregateWidget(callback) {
    var selector = '#looxReviews[data-loox-aggregate][data-product-id]';
    var match = document.querySelector(selector);
    if (match) return callback(match);

    var observer = new MutationObserver(function () {
      var found = document.querySelector(selector);
      if (!found) return;
      observer.disconnect();
      callback(found);
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });
    window.setTimeout(function () { observer.disconnect(); }, FIND_TIMEOUT);
  }

  function cacheKey(config) {
    return 'aa-emr:v2:' + config.productId + ':' + config.locale;
  }

  function cacheRead(config) {
    try {
      var raw = window.sessionStorage.getItem(cacheKey(config));
      if (!raw) return null;
      var cached = JSON.parse(raw);
      return Date.now() - cached.savedAt < CACHE_TTL ? cached.payload : null;
    } catch (error) {
      return null;
    }
  }

  function cacheWrite(config, payload) {
    try {
      window.sessionStorage.setItem(cacheKey(config), JSON.stringify({ savedAt: Date.now(), payload: payload }));
    } catch (error) {
      return;
    }
  }

  function isAllowedMedia(media) {
    if (!media || (media.type !== 'photo' && media.type !== 'video') || typeof media.url !== 'string') return false;
    try {
      var parsed = new URL(media.url);
      if (parsed.protocol !== 'https:') return false;
      if (media.type === 'photo') return parsed.hostname === 'images.loox.io';
      return parsed.hostname === 'iframe.videodelivery.net' || parsed.hostname.endsWith('.videodelivery.net') || parsed.hostname.endsWith('.cloudflarestream.com');
    } catch (error) {
      return false;
    }
  }

  function mediaReviews(payload, productId) {
    if (!payload || !Array.isArray(payload.reviews)) return [];
    return payload.reviews.reduce(function (result, review) {
      if (!review || !review.product || String(review.product.id) !== String(productId)) return result;
      var media = Array.isArray(review.media) ? review.media.filter(isAllowedMedia) : [];
      if (!media.length) return result;
      result.push({
        id: String(review.id || ''),
        rating: Math.max(1, Math.min(5, Number(review.rating) || 5)),
        body: typeof review.body === 'string' ? review.body.trim() : '',
        verified: review.verified === true,
        reviewer: review.reviewer && typeof review.reviewer.name === 'string' ? review.reviewer.name : '',
        date: typeof (review.date || review.createdAt) === 'string' ? (review.date || review.createdAt) : '',
        media: media
      });
      return result;
    }, []);
  }

  function request(config, attempt) {
    var controller = new AbortController();
    var timeout = window.setTimeout(function () { controller.abort(); }, REQUEST_TIMEOUT);
    var base = 'https://storefront-api.loox.io/storefront/v1/store/' + encodeURIComponent(config.publicStoreId);
    var query = '?product_id=' + encodeURIComponent(config.productId) + '&sort=featured&limit=250&locale=' + encodeURIComponent(config.locale || 'en');

    return fetch(base + '/product-reviews' + query, { signal: controller.signal, credentials: 'omit' })
      .then(function (response) {
        if (!response.ok) {
          var retryable = response.status >= 500;
          var error = new Error('Loox request failed');
          error.retryable = retryable;
          throw error;
        }
        return response.json();
      })
      .finally(function () { window.clearTimeout(timeout); })
      .catch(function (error) {
        if (attempt < 1 && (error.name === 'AbortError' || error.retryable || error instanceof TypeError)) {
          return request(config, attempt + 1);
        }
        throw error;
      });
  }

  function element(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (typeof text === 'string') node.textContent = text;
    return node;
  }

  function ratingLabel(copy, rating) {
    return copy.rating.replace('__RATING__', String(rating));
  }

  function stars(rating, copy) {
    var node = element('span', ROOT_CLASS + '__stars', '★★★★★'.slice(0, Math.round(rating)));
    node.setAttribute('role', 'img');
    node.setAttribute('aria-label', ratingLabel(copy, rating));
    return node;
  }

  function formatDate(review, config) {
    if (!review.date) return '';
    var date = new Date(review.date);
    if (Number.isNaN(date.getTime())) return '';
    try {
      return new Intl.DateTimeFormat(config.locale || 'en', { month: 'long', year: 'numeric' }).format(date);
    } catch (error) {
      return new Intl.DateTimeFormat('en', { month: 'long', year: 'numeric' }).format(date);
    }
  }

  function appendReviewContent(container, review, config, inDialog) {
    var meta = element('div', ROOT_CLASS + '__meta');
    var date = formatDate(review, config);
    meta.appendChild(stars(review.rating, config.copy));
    if (review.verified) meta.appendChild(element('span', ROOT_CLASS + '__verified', config.copy.verified));
    if (date) meta.appendChild(element('time', ROOT_CLASS + '__date', date));
    container.appendChild(meta);
    if (review.body) {
      container.appendChild(element('blockquote', inDialog ? ROOT_CLASS + '__dialog-quote' : ROOT_CLASS + '__quote', '“' + review.body + '”'));
    }
    if (review.reviewer) container.appendChild(element('p', ROOT_CLASS + '__reviewer', review.reviewer));
  }

  function mediaPreview(media, config) {
    var image = element('img', ROOT_CLASS + '__media');
    image.loading = 'lazy';
    image.decoding = 'async';
    image.alt = '';
    if (media.type === 'photo') {
      image.src = media.url;
    } else if (media.thumbnailUrl && isAllowedMedia({ type: 'photo', url: media.thumbnailUrl })) {
      image.src = media.thumbnailUrl;
    } else {
      image.removeAttribute('src');
    }
    return image;
  }

  function renderDialog(root, items, config) {
    var dialog = element('dialog', ROOT_CLASS + '__dialog');
    var panel = element('div', ROOT_CLASS + '__dialog-panel');
    var stage = element('div', ROOT_CLASS + '__dialog-stage');
    var details = element('div', ROOT_CLASS + '__dialog-content');
    var close = element('button', ROOT_CLASS + '__dialog-button ' + ROOT_CLASS + '__dialog-close', '×');
    var previous = element('button', ROOT_CLASS + '__dialog-button ' + ROOT_CLASS + '__dialog-prev', '‹');
    var next = element('button', ROOT_CLASS + '__dialog-button ' + ROOT_CLASS + '__dialog-next', '›');
    var activeIndex = 0;
    var returnFocus = null;
    var previousBodyOverflow = '';

    close.type = previous.type = next.type = 'button';
    close.setAttribute('aria-label', config.copy.close);
    previous.setAttribute('aria-label', config.copy.previous);
    next.setAttribute('aria-label', config.copy.next);

    function display(index) {
      activeIndex = (index + items.length) % items.length;
      while (stage.firstChild) stage.removeChild(stage.firstChild);
      while (details.firstChild) details.removeChild(details.firstChild);
      var item = items[activeIndex];
      var media = item.media;
      var mediaNode;
      if (media.type === 'photo') {
        mediaNode = element('img', ROOT_CLASS + '__dialog-media');
        mediaNode.src = media.url;
        mediaNode.alt = config.copy.openPhoto;
      } else if (media.url.indexOf('videodelivery.net') > -1 || media.url.indexOf('cloudflarestream.com') > -1) {
        mediaNode = element('iframe', ROOT_CLASS + '__dialog-media');
        mediaNode.src = media.url;
        mediaNode.title = config.copy.playVideo;
        mediaNode.allow = 'accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture';
        mediaNode.allowFullscreen = true;
      } else {
        mediaNode = element('video', ROOT_CLASS + '__dialog-media');
        mediaNode.src = media.url;
        mediaNode.controls = true;
      }
      stage.appendChild(mediaNode);
      appendReviewContent(details, item.review, config, true);
      previous.hidden = items.length < 2;
      next.hidden = items.length < 2;
    }

    close.addEventListener('click', function () { dialog.close(); });
    previous.addEventListener('click', function () { display(activeIndex - 1); });
    next.addEventListener('click', function () { display(activeIndex + 1); });
    dialog.addEventListener('close', function () {
      while (stage.firstChild) stage.removeChild(stage.firstChild);
      while (details.firstChild) details.removeChild(details.firstChild);
      document.body.style.overflow = previousBodyOverflow;
      if (returnFocus) returnFocus.focus();
    });
    dialog.addEventListener('click', function (event) {
      if (event.target === dialog) dialog.close();
    });
    dialog.addEventListener('keydown', function (event) {
      if (event.key !== 'Tab') return;
      var focusable = Array.prototype.slice.call(dialog.querySelectorAll('button:not([hidden]), [href], iframe, video[controls], [tabindex]:not([tabindex="-1"])'));
      if (!focusable.length) return;
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    panel.appendChild(stage);
    panel.appendChild(details);
    dialog.appendChild(panel);
    dialog.appendChild(close);
    dialog.appendChild(previous);
    dialog.appendChild(next);
    root.appendChild(dialog);

    return function open(index, opener) {
      returnFocus = opener;
      display(index);
      previousBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      if (typeof dialog.showModal === 'function') dialog.showModal();
      close.focus();
      track('aa_exact_media_review_opened', eventDetail(config, { media_index: activeIndex }));
      if (items[activeIndex].media.type === 'video') {
        track('aa_exact_media_review_video_played', eventDetail(config, { media_index: activeIndex }));
      }
    };
  }

  function render(config, reviews, looxNode) {
    if (!reviews.length || document.querySelector('.' + ROOT_CLASS)) return;
    var eligibleAssetCount = reviews.reduce(function (total, review) { return total + review.media.length; }, 0);
    track('aa_exact_media_reviews_eligible', eventDetail(config, {
      media_review_count: reviews.length,
      media_asset_count: eligibleAssetCount
    }));

    var section = element('section', ROOT_CLASS);
    section.setAttribute('aria-labelledby', 'aa-exact-media-reviews-heading');
    var inner = element('div', ROOT_CLASS + '__inner');
    var header = element('header', ROOT_CLASS + '__header');
    var eyebrow = element('p', ROOT_CLASS + '__eyebrow', config.copy.eyebrow);
    var heading = element('h2', ROOT_CLASS + '__heading', config.copy.heading);
    var support = element('p', ROOT_CLASS + '__support', config.copy.support);
    var grid = element('div', ROOT_CLASS + '__grid');
    var allMedia = [];
    var visibleReviews = reviews.slice(0, MAX_VISIBLE_REVIEWS);

    reviews.forEach(function (review) {
      review.mediaStartIndex = allMedia.length;
      review.media.forEach(function (media) {
        allMedia.push({ media: media, review: review });
      });
    });

    heading.id = 'aa-exact-media-reviews-heading';
    grid.dataset.count = String(visibleReviews.length);
    header.appendChild(eyebrow);
    header.appendChild(heading);
    header.appendChild(support);
    inner.appendChild(header);
    inner.appendChild(grid);
    section.appendChild(inner);

    visibleReviews.forEach(function (review) {
      var card = element('article', ROOT_CLASS + '__card');
      var button = element('button', ROOT_CLASS + '__media-button');
      var firstMedia = review.media[0];
      var mediaIndex = review.mediaStartIndex;
      button.type = 'button';
      button.setAttribute('aria-label', firstMedia.type === 'video' ? config.copy.playVideo : config.copy.openPhoto);
      button.appendChild(mediaPreview(firstMedia, config));
      button.appendChild(element('span', ROOT_CLASS + '__media-badge', firstMedia.type === 'video' ? '▶ ' + config.copy.video : config.copy.photo));
      if (review.media.length > 1) {
        button.appendChild(element('span', ROOT_CLASS + '__asset-count', config.copy.mediaCount.replace('__COUNT__', String(review.media.length))));
      }

      var content = element('div', ROOT_CLASS + '__content');
      appendReviewContent(content, review, config, false);
      card.appendChild(button);
      card.appendChild(content);
      grid.appendChild(card);
      button.dataset.mediaIndex = String(mediaIndex);
    });

    var footer = element('div', ROOT_CLASS + '__footer');
    var more = element('button', ROOT_CLASS + '__more', config.copy.moreReviews);
    more.type = 'button';
    more.addEventListener('click', function () {
      looxNode.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
      track('aa_exact_media_reviews_bridge_clicked', eventDetail(config, {
        media_review_count: reviews.length,
        media_asset_count: allMedia.length
      }));
    });
    footer.appendChild(more);
    inner.appendChild(footer);

    var looxSection = looxNode.closest('.shopify-section') || looxNode.parentElement;
    if (!looxSection || !looxSection.parentNode) return;
    var insertionTarget = looxSection;
    var precedingSection = looxSection.previousElementSibling;
    var allReviewsHeading = config.copy.allReviews.trim().toLowerCase();
    var featuredVideoHeading = config.copy.featuredVideoReviews.trim().toLowerCase();
    var sectionsChecked = 0;
    while (precedingSection && sectionsChecked < 12) {
      var precedingText = precedingSection.innerText.trim().toLowerCase();
      var sectionId = precedingSection.id || '';
      var isAllReviewsHeading = sectionId.endsWith('__rich_text_L9AaDy') || precedingText === allReviewsHeading;
      var isFeaturedVideoHeading = sectionId.endsWith('__rich_text_rLgV3A') || precedingText === featuredVideoHeading;
      if (isAllReviewsHeading) insertionTarget = precedingSection;
      if (isFeaturedVideoHeading) {
        insertionTarget = precedingSection;
        break;
      }
      precedingSection = precedingSection.previousElementSibling;
      sectionsChecked += 1;
    }
    insertionTarget.parentNode.insertBefore(section, insertionTarget);

    var openDialog = renderDialog(section, allMedia, config);
    grid.addEventListener('click', function (event) {
      var opener = event.target.closest('.' + ROOT_CLASS + '__media-button');
      if (!opener) return;
      openDialog(Number(opener.dataset.mediaIndex) || 0, opener);
    });
    track('aa_exact_media_reviews_rendered', eventDetail(config, {
      media_review_count: reviews.length,
      media_asset_count: allMedia.length
    }));
  }

  function load(config, looxNode) {
    var cached = cacheRead(config);
    if (cached) {
      if (!cached || !Array.isArray(cached.reviews)) {
        trackError(config, 'malformed_cached_response');
        return;
      }
      render(config, mediaReviews(cached, config.productId), looxNode);
      return;
    }
    request(config, 0).then(function (payload) {
      if (!payload || !Array.isArray(payload.reviews)) {
        trackError(config, 'malformed_api_response');
        return;
      }
      cacheWrite(config, payload);
      render(config, mediaReviews(payload, config.productId), looxNode);
    }).catch(function () { trackError(config, 'api_request_failed'); });
  }

  function init() {
    if (started) return;
    started = true;
    var config = parseConfig();
    if (!config) return;
    findAggregateWidget(function (looxNode) {
      if (String(looxNode.getAttribute('data-product-id')) !== String(config.productId)) {
        trackError(config, 'product_id_mismatch');
        return;
      }
      var trigger = looxNode.closest('.shopify-section') || looxNode;
      if (!('IntersectionObserver' in window)) return load(config, looxNode);
      var observer = new IntersectionObserver(function (entries) {
        if (!entries.some(function (entry) { return entry.isIntersecting; })) return;
        observer.disconnect();
        load(config, looxNode);
      }, { rootMargin: '600px 0px' });
      observer.observe(trigger);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
}());
